import {
  ALPHABET,
  ALPHABET_SIZE,
  letterAt,
  letterIndex,
  lettersOnly,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";
import { englishPairScore } from "@/lib/tool-kit/ciphers-misc/english-ngrams";

/**
 * The letter one-time pad: add a pad letter to each message letter, modulo 26, to
 * encrypt; subtract it to decrypt. A = 0 … Z = 25.
 *
 * The pad is random, as long as the message and used once. This module only does the
 * arithmetic and the random letters; whether a pad is used once is up to the person.
 */

/** Bytes below this value map evenly onto 26 letters (9 × 26); the rest are redrawn. */
const FAIR_LIMIT = 234;
const BATCH_FACTOR = 2;
const BATCH_EXTRA = 16;

/** Fill a buffer with cryptographically secure random bytes. */
export function secureRandomBytes(count: number): Uint8Array {
  const bytes = new Uint8Array(count);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * `length` random letters A–Z, drawn without bias: a random byte is used only if it is
 * below 234, so every letter has exactly the same chance. Call it from an event handler,
 * never while rendering.
 */
export function randomPad(
  length: number,
  randomBytes: (count: number) => Uint8Array = secureRandomBytes
): string {
  let pad = "";
  while (pad.length < length) {
    const wanted = length - pad.length;
    for (const byte of randomBytes(wanted * BATCH_FACTOR + BATCH_EXTRA)) {
      if (byte < FAIR_LIMIT && pad.length < length) {
        pad += ALPHABET[byte % ALPHABET_SIZE];
      }
    }
  }
  return pad;
}

/** Pad or message text reduced to A–Z capitals; accents folded, the rest dropped. */
export function padLetters(text: string): string {
  return lettersOnly(text);
}

/** "ABCDEFGHIJ" → "ABCDE FGHIJ": the way pads are printed. */
export function inFives(letters: string): string {
  return letters.match(/.{1,5}/g)?.join(" ") ?? "";
}

export interface PadStep {
  input: string;
  inputValue: number;
  key: string;
  keyValue: number;
  /** The sum or difference before reducing modulo 26. */
  raw: number;
  output: string;
}

export interface PadResult {
  output: string;
  steps: PadStep[];
  /** How many message letters had no pad letter left (0 when the pad is long enough). */
  shortBy: number;
}

/**
 * Combine `text` with `pad` starting at pad position `start` (from 0). `direction` is 1 to
 * encrypt (add) and −1 to decrypt (subtract). Both must already be clean A–Z letters.
 * Letters beyond the end of the pad are not enciphered; `shortBy` counts them.
 */
export function applyPad(
  text: string,
  pad: string,
  start: number,
  direction: 1 | -1
): PadResult {
  const steps: PadStep[] = [];
  let output = "";
  for (const [index, char] of [...text].entries()) {
    const key = pad[start + index];
    if (key === undefined) {
      break;
    }
    const inputValue = letterIndex(char);
    const keyValue = letterIndex(key);
    const raw = inputValue + direction * keyValue;
    const result = letterAt(raw);
    output += result;
    steps.push({ input: char, inputValue, key, keyValue, raw, output: result });
  }
  return { output, steps, shortBy: [...text].length - steps.length };
}

/** Letter-by-letter difference a − b (mod 26) over the length of the shorter text. */
export function letterDifference(a: string, b: string): string {
  const length = Math.min(a.length, b.length);
  let difference = "";
  for (let index = 0; index < length; index += 1) {
    difference += letterAt(
      mod(letterIndex(a[index]) - letterIndex(b[index]), ALPHABET_SIZE)
    );
  }
  return difference;
}

export interface CribPlacement {
  /** Where, counting from 0, the guessed word was laid against the other message. */
  offset: number;
  /** What the other message would read there if the guess is right. */
  fragment: string;
  score: number;
}

/**
 * Two messages encrypted with the same pad letters give C₁ − C₂ = P₁ − P₂: the pad
 * cancels. If a word is guessed to sit at some place in message 1, subtracting the
 * difference there reveals what message 2 says in the same place. Every position of the
 * guess is tried and ranked by how English the result looks, best first.
 */
export function cribDrag(difference: string, crib: string): CribPlacement[] {
  const placements: CribPlacement[] = [];
  for (let offset = 0; offset + crib.length <= difference.length; offset += 1) {
    const fragment = letterDifference(
      crib,
      difference.slice(offset, offset + crib.length)
    );
    placements.push({ offset, fragment, score: englishPairScore(fragment) });
  }
  return placements.sort((a, b) => b.score - a.score || a.offset - b.offset);
}
